import React, { useEffect, useState } from 'react';
import { buildLegacyFileUrl, getPresignedFileUrl } from '../../services/fileUrlService';
import { isInlineFileSrc, resolveLocalPreviewUrl } from '../../services/offlineAttachmentStore';

/**
 * Renders an img whose src is a presigned S3 URL (or legacy /api/files URL when S3 is off).
 */
const PresignedImage = ({
  fileKey,
  alt = '',
  className,
  style,
  onClick,
  onError,
  onLoad,
  loadingClassName = 'bg-gray-200 dark:bg-gray-700 animate-pulse',
  download = false,
  originalName,
  title,
}) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [legacyTried, setLegacyTried] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!fileKey) {
      setSrc(null);
      return undefined;
    }
    if (isInlineFileSrc(fileKey)) {
      setSrc(fileKey);
      setFailed(false);
      setLegacyTried(false);
      return undefined;
    }
    setSrc(null);
    setFailed(false);
    setLegacyTried(false);
    const opts = {};
    if (download) opts.download = true;
    if (originalName) opts.originalName = originalName;
    (async () => {
      try {
        const localUrl = await resolveLocalPreviewUrl(fileKey);
        if (localUrl) {
          if (!cancelled) setSrc(localUrl);
          return;
        }
        const url = await getPresignedFileUrl(fileKey, opts);
        if (!cancelled) setSrc(url || null);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileKey, download, originalName]);

  if (!fileKey) return null;

  if (!src) {
    return (
      <span
        className={`inline-block ${loadingClassName} ${className || ''}`}
        style={style}
        aria-hidden
      />
    );
  }

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400 rounded-lg cursor-pointer ${className || ''}`}
        style={style}
        onClick={onClick}
        title={title || originalName || "Failed to load preview"}
      >
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      title={title}
      className={className}
      style={style}
      onClick={onClick}
      onLoad={onLoad}
      onError={(e) => {
        if (onError) onError(e);
        else if (!legacyTried) {
          const opts = {};
          if (download) opts.download = true;
          if (originalName) opts.originalName = originalName;
          const legacyUrl = buildLegacyFileUrl(fileKey, opts);
          if (legacyUrl && legacyUrl !== src) {
            setLegacyTried(true);
            setSrc(legacyUrl);
            return;
          }
          setLegacyTried(true);
          setFailed(true);
          e.target.alt = 'Failed to load';
        } else if (!failed) {
          setFailed(true);
          e.target.alt = 'Failed to load';
        }
      }}
    />
  );
};

export default PresignedImage;
